import React, { useContext } from 'react';
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import PropTypes from 'prop-types';
import { PlanContext } from '../context/PlanContext';
import { FundType } from '../constants';

export const stepText = [
  'Choose Your Budget',
  'Core Fund Plan',
  'Focused Fund Plan',
  'Follow-On Fund Plan',
  'Syndications Plan',
  'Review Plan',
  'Review Plan with My Relationship Manager',
];

const Sidebar = ({
  currentStep,
  furthestStep,
  canNavigateForward,
  onStepClick,
}) => {
  const { plan, updatedPlan } = useContext(PlanContext);
  const activePlan = updatedPlan || plan;

  const getAllocations = () => {
    const coreFundAllocation = activePlan?.fundTypeAllocations?.find((fund) => fund.fundType === FundType.CORE)
      ?.amount || 0;
    const focusedFundAllocation = activePlan?.fundTypeAllocations?.find(
      (fund) => fund.fundType === FundType.FOCUSED,
    )?.amount || 0;
    const followOnFundAllocation = activePlan?.fundTypeAllocations?.find(
      (fund) => fund.fundType === FundType.FOLLOW_ON,
    )?.amount || 0;
    const syndicationFundAllocation = activePlan?.fundTypeAllocations?.find(
      (fund) => fund.fundType === FundType.SYNDICATION,
    )?.amount || 0;
    return {
      coreFundAllocation,
      focusedFundAllocation,
      followOnFundAllocation,
      syndicationFundAllocation,
    };
  };

  const isDisabled = (text) => {
    const {
      coreFundAllocation,
      focusedFundAllocation,
      followOnFundAllocation,
      syndicationFundAllocation,
    } = getAllocations();
    if (text === 'Core Fund Plan' && parseFloat(coreFundAllocation) === 0) {
      return true;
    }
    if (
      text === 'Focused Fund Plan'
      && parseFloat(focusedFundAllocation) === 0
    ) {
      return true;
    }
    if (
      text === 'Follow-On Fund Plan'
      && parseFloat(followOnFundAllocation) === 0
    ) {
      return true;
    }
    if (
      text === 'Syndications Plan'
      && parseFloat(syndicationFundAllocation) === 0
    ) {
      return true;
    }
    return false;
  };

  return (
    <Drawer
      variant="permanent"
      PaperProps={{
        sx: {
          backgroundColor: '#DDE9EF',
          width: '25%',
          borderRight: 'none',
          alignItems: 'center',
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ width: '100%', padding: ' 0 10%', alignItems: 'center' }}>
        <Typography
          variant="h1ExtraLarge"
          component="h1"
          sx={{
            marginTop: { lg: '40%', md: '50%' },
            wordBreak: 'break-word',
            padding: '20px',
          }}
        >
          Planning Steps
        </Typography>
        <List style={{ marginTop: '10%' }}>
          {stepText.map((text, index) => {
            const disabled = isDisabled(text);
            const isForwardNavigation = index > currentStep;
            const isReachable = index <= furthestStep
              && !disabled
              && (!isForwardNavigation || canNavigateForward);
            const isActive = index === currentStep;
            let textColor = '#979797';
            if (isActive) {
              textColor = 'white';
            } else if (isReachable) {
              textColor = '#7B5599';
            }

            return (
              <ListItemButton
                key={text}
                onClick={() => isReachable && onStepClick(index)}
                sx={{
                  backgroundColor: isActive ? '#7B5599' : 'inherit',
                  color: textColor,
                  cursor: isReachable ? 'pointer' : 'default',
                  borderRadius: '10px',
                  margin: '20px 0',
                  opacity: disabled && index <= furthestStep ? '0.38' : 1,
                  '&:hover': {
                    backgroundColor: isActive ? '#7B5599' : 'inherit',
                  },
                }}
              >
                <ListItemText
                  primary={(
                    <Typography
                      variant="h2"
                      component="h2"
                      style={{ color: textColor, display: 'flex' }}
                    >
                      <span style={{ height: '100%', flex: '0 0 30px' }}>{`${index + 1}`} | </span>
                      <span>{text}</span>
                    </Typography>
                  )}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};

Sidebar.propTypes = {
  currentStep: PropTypes.number.isRequired,
  furthestStep: PropTypes.number.isRequired,
  canNavigateForward: PropTypes.bool.isRequired,
  onStepClick: PropTypes.func.isRequired,
};

export default Sidebar;
